import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppNav from "@/components/AppNav";
import { useAuth } from "@/context/AuthContext";
import DesignPreview from "@/components/studio/DesignPreview";
import { Button } from "@/components/ui/button";
import { Star, Flame, Lightbulb, LayoutGrid, Trash2, Palette, ArrowLeft } from "lucide-react";

const ICONS = { standout: Star, bold: Flame, clever: Lightbulb, clean: LayoutGrid };

export default function GalleryPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [config, setConfig] = useState({});
  const [tab, setTab] = useState("class");
  const [busy, setBusy] = useState(null);

  const load = (mine) => api.get("/gallery", { params: mine ? { mine: true } : {} })
    .then((r) => { setPosts(r.data.posts); setKinds(r.data.reactions); })
    .catch(() => setPosts([]));

  useEffect(() => { load(tab === "mine"); }, [tab]);
  useEffect(() => { api.get("/studio/design").then((r) => setConfig(r.data.config)).catch(() => setConfig({})); }, []);

  const react = async (post_id, kind) => {
    setBusy(`${post_id}-${kind}`);
    try {
      const r = await api.post(`/gallery/${post_id}/react`, { kind });
      setPosts((ps) => ps.map((p) => (p.post_id === post_id ? r.data : p)));
    } catch { toast.error("Couldn't save that reaction."); } finally { setBusy(null); }
  };

  const remove = async (post_id) => {
    if (!window.confirm("Remove this design from the gallery? This can't be undone.")) return;
    try {
      await api.delete(`/gallery/${post_id}`);
      setPosts((ps) => ps.filter((p) => p.post_id !== post_id));
      toast.success("Removed from the gallery.");
    } catch { toast.error("Couldn't remove that post."); }
  };

  const top = useMemo(() => {
    if (!posts?.length) return null;
    const score = (p) => Object.values(p.counts || {}).reduce((a, b) => a + b, 0);
    const best = [...posts].sort((a, b) => score(b) - score(a))[0];
    return score(best) > 0 ? best : null;
  }, [posts]);

  return (
    <div className="min-h-screen bg-background">
      <AppNav />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
        <Link to="/studio/design" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="gallery-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to the Studio
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#DB2777] font-mono-data mb-2">Class Gallery Wall</p>
            <h1 data-testid="gallery-title" className="font-display text-4xl sm:text-5xl tracking-tight inline-flex items-center gap-3">
              <Palette className="w-8 h-8 text-[#DB2777]" /> The Gallery
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Published designs from your crew. Browse the work, then leave a reaction on the pieces that stand out.
            </p>
          </div>
          <div className="flex gap-1.5 p-1 rounded-full bg-white/5 border border-white/10">
            {[["class", "My class"], ["mine", "My posts"]].map(([id, label]) => (
              <button key={id} data-testid={`gallery-tab-${id}`} onClick={() => setTab(id)}
                className={`px-4 h-9 rounded-full text-sm transition-colors ${tab === id ? "bg-[#DB2777] text-white" : "text-slate-300 hover:bg-white/10"}`}>{label}</button>
            ))}
          </div>
        </div>

        {top && tab === "class" && (
          <p data-testid="gallery-top-banner" className="mb-5 text-xs text-slate-300 inline-flex items-center gap-2 rounded-full bg-[#DB2777]/10 border border-[#DB2777]/30 px-3 py-1.5">
            <Star className="w-3.5 h-3.5 text-[#DB2777]" /> Most-loved right now: <b>{top.title}</b> by {top.author_name}
          </p>
        )}

        {posts === null && <p className="text-sm text-muted-foreground" data-testid="gallery-loading">Loading the wall…</p>}

        {posts && posts.length === 0 && (
          <div data-testid="gallery-empty" className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center">
            <Palette className="w-10 h-10 text-[#DB2777] mx-auto mb-4 opacity-70" />
            <p className="text-base font-medium">Nothing on the wall yet</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
              Finish a Graphic Design lesson, then press <b>Publish to Gallery</b> to put your work up for the class to see.
            </p>
            <Button asChild className="mt-5 bg-[#DB2777] text-white hover:bg-[#f472b6]"><Link to="/studio/design" data-testid="gallery-go-studio">Open Graphic Design</Link></Button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {(posts || []).map((p) => (
            <div key={p.post_id} data-testid={`gallery-card-${p.post_id}`} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[#DB2777]/40 transition-colors">
              <DesignPreview doc={p.doc} config={config} height={200} testid={`gallery-preview-${p.post_id}`} />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-base font-medium truncate" data-testid={`gallery-card-title-${p.post_id}`}>{p.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {p.author_name === user?.name ? "You" : p.author_name} · {p.mission_title}
                      {p.score != null && <span className="text-[#22D3EE]"> · {p.score}%</span>}
                    </p>
                  </div>
                  {p.can_delete && (
                    <button data-testid={`gallery-delete-${p.post_id}`} onClick={() => remove(p.post_id)} title="Remove from the gallery"
                      className="p-1.5 rounded-md text-slate-500 hover:text-[#E11D48] hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                {p.caption && <p className="text-sm text-slate-300 mt-2">{p.caption}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3.5">
                  {(kinds || []).map((k) => {
                    const Icon = ICONS[k.id] || Star;
                    const on = (p.mine || []).includes(k.id);
                    return (
                      <button key={k.id} data-testid={`gallery-react-${k.id}-${p.post_id}`} disabled={busy === `${p.post_id}-${k.id}`}
                        onClick={() => react(p.post_id, k.id)} title={k.label}
                        className={`h-8 px-2.5 rounded-full inline-flex items-center gap-1.5 text-xs border transition-colors ${on ? "bg-[#DB2777]/20 border-[#DB2777]/50 text-[#f9a8d4]" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                        <Icon className="w-3.5 h-3.5" /> {k.label}
                        <span className="font-mono-data" data-testid={`gallery-count-${k.id}-${p.post_id}`}>{(p.counts || {})[k.id] || 0}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
